'use client';

import { css, cx } from '@repo/pandacss/styled-system/css';
import { flex } from '@repo/pandacss/styled-system/patterns';
import { MouseEvent, ReactNode, TouchEvent, useRef, useState } from 'react';
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md';

type SlideshowProps = {
  children: ReactNode[];
};

const DRAG_OFFSET = 250;

const sharedButtonClass = css({
  cursor: 'pointer',
  borderTopStyle: 'none',
  borderRightStyle: 'none',
  borderBottomStyle: 'none',
  borderLeftStyle: 'none',
  paddingTop: '0',
  paddingRight: '0',
  paddingBottom: '0',
  paddingLeft: '0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '9',
  width: '9',
  backgroundColor: 'neutral.white',
  borderTopLeftRadius: 'half',
  borderTopRightRadius: 'half',
  borderBottomRightRadius: 'half',
  borderBottomLeftRadius: 'half',
  color: 'secondary',
  position: 'absolute',
  top: '[40%]',
  zIndex: '[10]',
  boxShadow: '[0 4px 8px 0 rgba(100,102,106,.1),0 1px 4px 0 rgba(100,102,106,.36)]',
  fontSize: 'xxl',
});

export function Slideshow({ children }: SlideshowProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const dragStartX = useRef(0);
  const totalSlides = children.length;

  if (totalSlides === 0) return null;

  const handlePrev = () => {
    setCurrentIndex(prevIndex => (prevIndex > 0 ? prevIndex - 1 : prevIndex));
  };

  const handleNext = () => {
    setCurrentIndex(prevIndex => (prevIndex < totalSlides - 1 ? prevIndex + 1 : prevIndex));
  };

  const isTouchEvent = (evt: MouseEvent | TouchEvent): evt is TouchEvent => {
    return (evt as TouchEvent).touches !== undefined;
  };

  const getEvtClientX = (evt: MouseEvent | TouchEvent) => {
    return isTouchEvent(evt) ? evt.touches[0]?.clientX : evt.clientX;
  };

  const handleDragStart = (evt: MouseEvent | TouchEvent) => {
    dragStartX.current = getEvtClientX(evt);
    setIsDragging(true);
  };

  const handleDragMove = (evt: MouseEvent | TouchEvent) => {
    if (!isDragging) return;
    const currentX = getEvtClientX(evt);
    setDragOffset(currentX - dragStartX.current);
  };

  const handleDragEnd = () => {
    if (!isDragging || dragStartX.current === 0) return;
    setIsDragging(false);
    if (dragOffset > DRAG_OFFSET) {
      handlePrev();
    } else {
      handleNext();
    }
    setDragOffset(0);
    dragStartX.current = 0;
  };

  return (
    <div>
      <div className={css({ position: 'relative' })}>
        <div className={css({ display: { base: 'none', sm: 'block' } })}>
          <div
            className={css({
              transitionProperty: '[opacity]',
              transitionDuration: '[0.3s]',
              transitionTimingFunction: '[ease-in-out]',
            })}
            style={{
              opacity: currentIndex > 0 ? 1 : 0,
              pointerEvents: currentIndex > 0 ? 'auto' : 'none',
            }}
          >
            <button
              aria-label="Previous slide"
              onClick={handlePrev}
              className={cx(
                sharedButtonClass,
                css({
                  borderTopRightRadius: 'half',
                  borderBottomRightRadius: 'half',
                  left: { base: '[-0.5rem]', md: '[-1.5rem]' },
                }),
              )}
            >
              <MdKeyboardArrowLeft />
            </button>
          </div>
        </div>

        <div
          className={css({
            position: 'relative',
            width: 'full',
            overflowX: 'hidden',
            overflowY: 'hidden',
            userSelect: 'none',
          })}
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          onMouseMove={handleDragMove}
          onTouchMove={handleDragMove}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
          onTouchEnd={handleDragEnd}
        >
          <div
            className={flex({ align: 'stretch' })}
            style={{
              transition: isDragging ? 'none' : 'transform 0.5s ease-in-out',
              transform: `translateX(calc(-${(currentIndex * 100) / totalSlides}% + ${dragOffset}px))`,
              width: `${totalSlides * 100}%`,
            }}
          >
            {children.map((child, index) => (
              <div key={index} className={css({ paddingRight: '4', width: 'full' })}>
                {child}
              </div>
            ))}
          </div>
        </div>

        <div className={css({ display: { base: 'none', sm: 'block' } })}>
          <div
            className={css({
              transitionProperty: '[opacity]',
              transitionDuration: '[0.3s]',
              transitionTimingFunction: '[ease-in-out]',
            })}
            style={{
              opacity: currentIndex < totalSlides - 1 ? 1 : 0,
              pointerEvents: currentIndex < totalSlides - 1 ? 'auto' : 'none',
            }}
          >
            <button
              aria-label="Next slide"
              onClick={handleNext}
              className={cx(
                sharedButtonClass,
                css({
                  borderTopLeftRadius: 'half',
                  borderBottomLeftRadius: 'half',
                  right: '0',
                }),
              )}
            >
              <MdKeyboardArrowRight />
            </button>
          </div>
        </div>
      </div>

      {totalSlides > 1 && (
        <div className={flex({ justify: 'center', marginTop: '7' })}>
          {Array.from({ length: totalSlides }, (_, index) => (
            <div
              key={`slider-${index}`}
              role="button"
              tabIndex={0}
              aria-label={`Go to slide ${index + 1}`}
              onClick={() => setCurrentIndex(index)}
              className={css({ width: '12', height: '12', cursor: 'pointer' })}
            >
              <div
                data-active={index === currentIndex}
                className={css({
                  borderTopColor: 'neutral.main',
                  borderRightColor: 'neutral.main',
                  borderBottomColor: 'neutral.main',
                  borderLeftColor: 'neutral.main',
                  borderTopStyle: 'solid',
                  borderRightStyle: 'solid',
                  borderBottomStyle: 'solid',
                  borderLeftStyle: 'solid',
                  height: '2.5',
                  width: '2.5',
                  borderTopLeftRadius: 'half',
                  borderTopRightRadius: 'half',
                  borderBottomRightRadius: 'half',
                  borderBottomLeftRadius: 'half',
                  transitionProperty: '[background-color]',
                  transitionDuration: '[0.2s]',
                  transitionTimingFunction: '[ease-in, background-color 0.5s ease-out]',
                  '&[data-active="true"]': {
                    backgroundColor: 'primary',
                    borderTopWidth: 'none',
                    borderRightWidth: 'none',
                    borderBottomWidth: 'none',
                    borderLeftWidth: 'none',
                  },
                  '&[data-active="false"]': {
                    backgroundColor: 'transparent',
                    borderTopWidth: 'thin',
                    borderRightWidth: 'thin',
                    borderBottomWidth: 'thin',
                    borderLeftWidth: 'thin',
                  },
                })}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
